import React, { useState, useRef, useEffect } from 'react';
import { User, Entrepreneur, Investor } from '../types';
import { Building2, DollarSign, Users, TrendingUp, Award, MapPin, Briefcase } from 'lucide-react';

interface ProfileCardProps {
  user: User;
  onSwipe: (direction: 'left' | 'right') => void;
  isTop: boolean;
}

export const ProfileCard: React.FC<ProfileCardProps> = ({ user, onSwipe, isTop }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [rotation, setRotation] = useState(0);
  const cardRef = useRef<HTMLDivElement>(null);
  const startPos = useRef({ x: 0, y: 0 });

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!isTop) return;
    setIsDragging(true);
    startPos.current = { x: e.clientX, y: e.clientY };
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (!isTop) return;
    setIsDragging(true);
    const touch = e.touches[0];
    startPos.current = { x: touch.clientX, y: touch.clientY };
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging || !isTop) return;
      
      const deltaX = e.clientX - startPos.current.x;
      const deltaY = e.clientY - startPos.current.y;
      
      setDragOffset({ x: deltaX, y: deltaY });
      setRotation(deltaX * 0.1);
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!isDragging || !isTop) return;
      
      const touch = e.touches[0];
      const deltaX = touch.clientX - startPos.current.x;
      const deltaY = touch.clientY - startPos.current.y;
      
      setDragOffset({ x: deltaX, y: deltaY });
      setRotation(deltaX * 0.1);
    };

    const handleEnd = () => {
      if (!isDragging || !isTop) return;
      
      const threshold = 100;
      if (Math.abs(dragOffset.x) > threshold) {
        onSwipe(dragOffset.x > 0 ? 'right' : 'left');
      }
      
      setIsDragging(false);
      setDragOffset({ x: 0, y: 0 });
      setRotation(0);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleEnd);
      document.addEventListener('touchmove', handleTouchMove);
      document.addEventListener('touchend', handleEnd);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleEnd);
    };
  }, [isDragging, dragOffset.x, isTop, onSwipe]);

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % user.images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + user.images.length) % user.images.length);
  };

  const cardStyle = {
    transform: `translate(${dragOffset.x}px, ${dragOffset.y}px) rotate(${rotation}deg)`,
    opacity: isTop ? 1 - Math.abs(dragOffset.x) / 300 : 1,
    zIndex: isTop ? 10 : 1,
    transition: isDragging ? 'none' : 'all 0.3s ease-out',
  };

  return (
    <div
      ref={cardRef}
      className={`absolute inset-0 bg-white rounded-2xl shadow-2xl overflow-hidden cursor-grab ${
        isDragging ? 'cursor-grabbing' : ''
      } ${!isTop ? 'scale-95' : ''}`}
      style={cardStyle}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
    >
      {/* Image indicators */}
      <div className="absolute top-4 left-4 right-4 flex gap-1 z-20">
        {user.images.map((_, index) => (
          <div
            key={index}
            className={`flex-1 h-1 rounded-full transition-colors duration-200 ${
              index === currentImageIndex ? 'bg-white' : 'bg-white/30'
            }`}
          />
        ))}
      </div>

      {/* Main image */}
      <div className="relative h-full">
        <img
          src={user.images[currentImageIndex]}
          alt={user.name}
          className="w-full h-full object-cover object-center"
        />
        
        {/* Image navigation areas */}
        <div
          className="absolute left-0 top-0 w-1/3 h-full z-10"
          onClick={prevImage}
        />
        <div
          className="absolute right-0 top-0 w-1/3 h-full z-10"
          onClick={nextImage}
        />

        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

        {/* User info */}
        <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
          <div className="space-y-4">
            {/* Header with name and type */}
            <div>
              <h2 className="text-2xl font-bold mb-2">{user.name}, {user.age}</h2>
              <div className="flex items-center gap-3">
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  user.userType === 'entrepreneur' 
                    ? 'bg-green-500 text-white' 
                    : 'bg-blue-500 text-white'
                }`}>
                  {user.userType === 'entrepreneur' ? 'Entrepreneur' : 'Investor'}
                </span>
                <div className="flex items-center gap-1 text-sm opacity-90">
                  <MapPin className="w-3 h-3" />
                  <span>{user.location}</span>
                </div>
              </div>
            </div>

            {/* Entrepreneur specific info */}
            {user.userType === 'entrepreneur' && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  <span className="font-semibold">{(user as Entrepreneur).company}</span>
                  <span className="text-sm opacity-80">• {(user as Entrepreneur).industry}</span>
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-1">
                    <DollarSign className="w-3 h-3" />
                    <span>{(user as Entrepreneur).fundingNeeded}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Users className="w-3 h-3" />
                    <span>{(user as Entrepreneur).teamSize} team</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <TrendingUp className="w-3 h-3" />
                    <span className="capitalize">{(user as Entrepreneur).stage}</span>
                  </div>
                </div>
                {user.bio && (
                  <p className="text-sm opacity-90 leading-relaxed">{user.bio}</p>
                )}
              </div>
            )}

            {/* Investor specific info */}
            {user.userType === 'investor' && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Briefcase className="w-4 h-4" />
                  <span className="font-semibold capitalize">{(user as Investor).investorType} Investor</span>
                  <span className="text-sm opacity-80">• {(user as Investor).portfolioSize} investments</span>
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-1">
                    <DollarSign className="w-3 h-3" />
                    <span>{(user as Investor).investmentRange}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Award className="w-3 h-3" />
                    <span>{(user as Investor).focusIndustries.slice(0, 2).join(', ')}</span>
                  </div>
                </div>
                {user.bio && (
                  <p className="text-sm opacity-90 leading-relaxed">{user.bio}</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Swipe indicators */}
        {isDragging && (
          <>
            <div
              className={`absolute inset-0 flex items-center justify-center transition-opacity ${
                dragOffset.x > 50 ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <div className="bg-green-500 text-white px-6 py-3 rounded-lg text-xl font-bold transform rotate-12">
                INTERESTED
              </div>
            </div>
            <div
              className={`absolute inset-0 flex items-center justify-center transition-opacity ${
                dragOffset.x < -50 ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <div className="bg-red-500 text-white px-6 py-3 rounded-lg text-xl font-bold transform -rotate-12">
                PASS
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};